import Head from 'next/head'
import React from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'
import { Input, InputGroup, InputRightAddon } from '@wartek-id/input'

import TopicFilter from '../components/TopicFilter'
import Card from '../partials/Topic/TopicCard'
import TopicPlaceholder from '../partials/Topic/TopicPlaceholder'
import GenericList from '../components/GenericList'
import {
  EmptyState,
  EmptyStateAction,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import TabbedNav from '../components/TabbedNav'
import { determineBadge, shouldHideNav } from '../helper'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useAPITopicList } from 'api/microlearning/useAPITopicList'
import { useSETrackerEvent } from '../hooks/useSETrackEvent'
import InfoUpdatedTopic from '../components/InfoUpdatedTopic'
import PenutupanTopikBanner from 'app/PelatihanMandiriPage/components/PenutupanTopikBanner'
import DesktopLandingShell from 'app/PelatihanMandiriPage/components/DesktopLandingShell'
import { usePelatihanMandiriDesktopView } from 'app/PelatihanMandiriPage/hooks/usePelatihanMandiriDesktopView'
import { useWindowDimensions } from 'utils/hooks/useWindowDimensions'

import type { TTopicList } from 'types/microlearning'

import TopicNotFound from 'assets/images/webinar-not-found.svg'

const SearchInput = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => {
  const [localValue, setLocalValue] = React.useState(value)

  React.useEffect(() => {
    const timer = setTimeout(() => onChange(localValue), 300)
    return () => clearTimeout(timer)
  }, [localValue, onChange])

  return (
    <InputGroup>
      <Input
        type="text"
        placeholder="Cari topik.."
        value={localValue}
        clearable
        onChange={(e) => setLocalValue(e.target.value)}
      />
      <InputRightAddon>
        <Icon>search</Icon>
      </InputRightAddon>
    </InputGroup>
  )
}

const TopicListRevamp = ({ isDesktop = false, currentId = null }) => {
  const { SETrackTopicList, SETrackChooseOrder } = useSETrackerEvent()
  const isPelatihanMandiriDesktopViewEnabled = usePelatihanMandiriDesktopView()
  const shouldUseDesktopView = isDesktop && isPelatihanMandiriDesktopViewEnabled
  const useFilter = useLocalStorage('topic-sort-filter', 'recommendation')
  const filterValue = useFilter[0] || 'recommendation'
  const { width } = useWindowDimensions()

  const [isSheetOpen, setIsSheetOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const { data: topicListData, status: topicListStatus } = useAPITopicList({
    sort: filterValue,
  })

  const filteredTopics = React.useMemo(() => {
    const list = topicListData?.data || []
    const query = searchQuery.trim().toLowerCase()
    if (!query) return list
    return list.filter(
      (topic) =>
        topic.name?.toLowerCase().includes(query) ||
        topic.description?.toLowerCase().includes(query)
    )
  }, [topicListData, searchQuery])

  const isSearching = searchQuery.trim().length > 0

  const emptyComponent = isSearching ? (
    <EmptyStateWithIllustration
      fullscreen={false}
      className="max-w-[400px] min-h-[520px] items-center px-0 text-center mx-auto"
      title="Topik tidak ditemukan"
      description="Coba kata kunci lain."
    >
      <TopicNotFound
        data-testid="topic-empty-search-image"
        as="EmptyStateImageSvg"
      />
    </EmptyStateWithIllustration>
  ) : (
    <EmptyState
      title="Konten belum tersedia"
      description="Belum ada topik yang dapat dipelajari saat ini; silakan coba lagi nanti."
    />
  )

  const STATE_FILTER = {
    recommendation: 'Rekomendasi',
    newest: 'Paling Baru',
    oldest: 'Paling lama',
    'almost-done': 'Hampir selesai',
    'just-started': 'Baru mulai',
    title: 'Judul A-Z',
  }

  const handleTracker = (item: TTopicList) => {
    SETrackTopicList({
      action_type: 'choose_topik',
      action_filter: filterValue,
      topic_id: item.id,
    })
  }

  const selectFilter = (value: string) => {
    useFilter[1](value)
    setIsSheetOpen(false)

    SETrackChooseOrder({
      action_type: 'choose_order',
      action_filter: value,
    })
  }

  const renderMasonry = (
    data: TTopicList[],
    renderItem: (item: TTopicList, index: number) => React.ReactNode,
    listKey: (item: TTopicList) => string | number
  ) => {
    let numCols = 1
    if (width >= 1280) numCols = 3
    else if (width >= 1024) numCols = 2

    const columns: TTopicList[][] = Array.from({ length: numCols }, () => [])
    data.forEach((item, index) => {
      columns[index % numCols].push(item)
    })

    return (
      <div className="flex w-full gap-4 items-start">
        {columns.map((col, colIndex) => (
          <div
            key={`col-${colIndex}`}
            className="flex flex-col flex-1 gap-4 min-w-0"
          >
            {col.map((item) => (
              <div key={listKey(item)}>
                {renderItem(item, data.indexOf(item))}
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  const RenderTopicList = () => (
    <GenericList
      data={filteredTopics}
      dataState={topicListStatus}
      listKey={({ id }) => id}
      errorComponent={
        <EmptyState
          title="Data gagal dimuat"
          description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
        >
          <EmptyStateAction onClick={() => window.location.reload()}>
            Muat Ulang
          </EmptyStateAction>
        </EmptyState>
      }
      emptyComponent={emptyComponent}
      loadingComponent={<TopicPlaceholder amount={4} />}
      renderItem={(item) => (
        <div onClick={() => handleTracker(item)}>
          <Card
            topicId={item.id}
            isFetchingProgress={topicListStatus !== 'success'}
            name={item.name}
            certifiable={item.certifiable}
            totalModule={item.totalModule}
            description={item.description}
            href={`/${
              process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
              'pelatihan-mandiri'
            }/topik/${item.id}?filter=${filterValue}`}
            image={item.headerImage}
            badge={determineBadge(item) as any}
            active={String(item.id) === currentId}
          />
        </div>
      )}
      itemClassName="mb-2"
    />
  )

  if (shouldUseDesktopView) {
    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Daftar Topik</title>
        </Head>
        <DesktopLandingShell activeMenu="topik">
          <section className="rounded-xl bg-gray-5 p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <Text variant="display-sm" as="h2" className="self-baseline">
                Daftar Topik
              </Text>
              <Button
                color="white"
                size="sm"
                onClick={() => setIsSheetOpen(true)}
              >
                <div className="flex items-center">
                  <div className="mr-1">
                    <Icon>sort</Icon>
                  </div>
                  <div>
                    Urutkan:{' '}
                    <b className="text-blue-60">
                      {STATE_FILTER[filterValue] ||
                        STATE_FILTER['recommendation']}
                    </b>
                  </div>
                </div>
              </Button>
            </div>
            <TopicFilter
              isOpen={isSheetOpen}
              selected={filterValue}
              onClose={() => setIsSheetOpen(false)}
              onSelect={(value) => selectFilter(value)}
              isDesktop={shouldUseDesktopView}
            />
            <div className="mb-5">
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            <div data-testid="pelatihan-mandiri-topic-grid">
              <GenericList
                data={filteredTopics}
                dataState={topicListStatus}
                listKey={({ id }) => id}
                customListRenderer={renderMasonry as any}
                errorComponent={
                  <EmptyState
                    title="Data gagal dimuat"
                    description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
                    fullscreen={false}
                  >
                    <EmptyStateAction
                      className="mb-4 max-w-sm"
                      onClick={() => window.location.reload()}
                    >
                      Muat Ulang
                    </EmptyStateAction>
                  </EmptyState>
                }
                emptyComponent={emptyComponent}
                loadingComponent={<TopicPlaceholder amount={6} />}
                renderItem={(item) => (
                  <div onClick={() => handleTracker(item)}>
                    <Card
                      topicId={item.id}
                      isFetchingProgress={topicListStatus !== 'success'}
                      name={item.name}
                      certifiable={item.certifiable}
                      totalModule={item.totalModule}
                      description={item.description}
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/topik/${item.id}?filter=${filterValue}`}
                      image={item.headerImage}
                      badge={determineBadge(item) as any}
                      active={String(item.id) === currentId}
                    />
                  </div>
                )}
              />
            </div>
          </section>
        </DesktopLandingShell>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Daftar Topik</title>
      </Head>
      {!shouldHideNav() && (
        <TabbedNav activeMenu="topik" className="top-[56px]" />
      )}
      <div
        className="bg-gray-10 pt-[34px]"
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        <div>
          <div
            className={clsx(shouldHideNav() ? 'pt-3' : 'pt-0') + ' relative'}
          >
            <div>
              <InfoUpdatedTopic parentClass="pt-2 mb-4" />
            </div>

            <PenutupanTopikBanner />

            <div className={clsx('p-3 sticky top-[90px] z-10 drop-shadow-md')}>
              <Button
                color="white"
                fullWidth
                onClick={() => setIsSheetOpen(true)}
              >
                <div className="flex items-center">
                  <div className="mr-1">
                    <Icon>sort</Icon>
                  </div>
                  <div>
                    Urutkan:{' '}
                    <b className="text-blue-60">
                      {STATE_FILTER[filterValue] ||
                        STATE_FILTER['recommendation']}
                    </b>
                  </div>
                </div>
              </Button>
            </div>

            <TopicFilter
              isOpen={isSheetOpen}
              selected={filterValue}
              onClose={() => setIsSheetOpen(false)}
              onSelect={(value) => selectFilter(value)}
              isDesktop={shouldUseDesktopView}
            />
            <div className="px-3 pb-5 pt-2">
              <div className="mb-4">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              <RenderTopicList />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TopicListRevamp
